import React, { useEffect, useState, useRef } from "react";
import { motion } from "motion/react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import postPaidManagement from "../../api/PostPaidManagement";
import clientRequests from "../../api/ClientRequests";
import Navbar from "../../components/dashboard/Navbar";
import {
  ArrowLeft,
  Plus,
  Calculator,
  DollarSign,
  Hash,
  Upload,
  Save,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Search,
  X,
  Mail,
  Phone,
  CheckCircle,
  Image as ImageIcon,
  Trash2,
  UserPlus,
  Users,
} from "lucide-react";

const CreatePostPaidRequest = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchedUsers, setSearchedUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserList, setShowUserList] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [formData, setFormData] = useState({
    user_slug: "",
    amount: "",
    sms_units: "",
    per_unit_price: "",
    receipt_url: "",
  });
  const [receiptImage, setReceiptImage] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [errors, setErrors] = useState({});
  const [calculationMode, setCalculationMode] = useState(null);
  const searchTimeoutRef = useRef(null);
  const searchInputRef = useRef(null);

  // Normalize phone number (handle both 233 and 0 prefix)
  const normalizePhoneNumber = (phone) => {
    if (!phone) return phone;
    let cleaned = phone.replace(/\D/g, "");
    if (cleaned.startsWith("0")) {
      cleaned = "233" + cleaned.substring(1);
    }
    if (!cleaned.startsWith("233")) {
      cleaned = "233" + cleaned;
    }
    return cleaned;
  };

  // Search users
  const searchUsers = async (query) => {
    if (!query || query.trim().length < 2) {
      setSearchedUsers([]);
      setSearchError(null);
      setShowUserList(false);
      return;
    }

    setSearching(true);
    setSearchError(null);
    setShowUserList(true);

    try {
      let searchTerm = query.trim();
      if (/^[\d\s+\-()]+$/.test(searchTerm)) {
        searchTerm = normalizePhoneNumber(searchTerm);
      }

      const response = await clientRequests.searchUsers(searchTerm);

      if (response.status && response.data?.data?.status_code === "000") {
        const usersArray = response.data.data.data;
        if (Array.isArray(usersArray) && usersArray.length > 0) {
          setSearchedUsers(usersArray);
          setSearchError(null);
        } else {
          setSearchedUsers([]);
          setSearchError(
            "No users found. Please try a different email or phone number."
          );
        }
      } else {
        setSearchedUsers([]);
        setSearchError(
          response.data?.data?.reason ||
            response.message ||
            "No users found. Please try a different email or phone number."
        );
      }
    } catch (error) {
      console.error("Error searching users:", error);
      setSearchedUsers([]);
      setSearchError(
        "Error searching for users. Please try a different email or phone number."
      );
    } finally {
      setSearching(false);
    }
  };

  // Handle search input change with debouncing
  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setSelectedUser(null);
    setSearchError(null);
    setFormData((prev) => ({ ...prev, user_slug: "" }));

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for debouncing (500ms delay)
    searchTimeoutRef.current = setTimeout(() => {
      if (value.trim().length >= 2) {
        searchUsers(value);
      } else {
        setSearchedUsers([]);
        setShowUserList(false);
      }
    }, 500);
  };

  // Select a user
  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setFormData((prev) => ({
      ...prev,
      user_slug: user.user_slug || "",
    }));
    setSearchQuery(user.email || user.phone_number || user.full_name || "");
    setShowUserList(false);
    setSearchError(null);
    toast.success(`User "${user.full_name || user.email}" selected`);
  };

  // Clear search and selection
  const clearSearch = () => {
    setSearchQuery("");
    setSelectedUser(null);
    setSearchedUsers([]);
    setShowUserList(false);
    setSearchError(null);
    setFormData((prev) => ({ ...prev, user_slug: "" }));
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchInputRef.current?.focus();
  };

  // Handle click outside to close user list
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target) &&
        !event.target.closest(".user-list-container")
      ) {
        setShowUserList(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Cleanup timeout and preview URL on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (receiptPreview) {
        URL.revokeObjectURL(receiptPreview);
      }
    };
  }, [receiptPreview]);

  // Convert image to base64
  const convertImageToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve(reader.result);
      };
      reader.onerror = (error) => {
        reject(error);
      };
      reader.readAsDataURL(file);
    });
  };

  // Handle receipt image upload
  const handleReceiptUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload a valid image file");
      setErrors((prev) => ({
        ...prev,
        receipt_url: "Please upload a valid image file",
      }));
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error("Image size must be less than 5MB");
      setErrors((prev) => ({
        ...prev,
        receipt_url: "Image size must be less than 5MB",
      }));
      return;
    }

    setUploadingImage(true);
    setErrors((prev) => ({ ...prev, receipt_url: "" }));

    try {
      // Create preview
      const previewUrl = URL.createObjectURL(file);
      setReceiptPreview(previewUrl);
      setReceiptImage(file);

      // Convert to base64
      const base64String = await convertImageToBase64(file);
      setFormData((prev) => ({
        ...prev,
        receipt_url: base64String,
      }));

      toast.success("Receipt image uploaded successfully");
    } catch (error) {
      console.error("Error converting image:", error);
      toast.error("Failed to process image. Please try again.");
      setErrors((prev) => ({
        ...prev,
        receipt_url: "Failed to process image",
      }));
      setReceiptPreview(null);
      setReceiptImage(null);
    } finally {
      setUploadingImage(false);
    }
  };

  // Remove receipt image
  const handleRemoveReceipt = () => {
    if (receiptPreview) {
      URL.revokeObjectURL(receiptPreview);
    }
    setReceiptPreview(null);
    setReceiptImage(null);
    setFormData((prev) => ({
      ...prev,
      receipt_url: "",
    }));
    setErrors((prev) => ({ ...prev, receipt_url: "" }));
  };

  // Bidirectional calculation: amount = sms_units * per_unit_price
  const handleFieldChange = (field, value) => {
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }

    const perUnitPrice = field === "per_unit_price" ? parseFloat(value) || 0 : parseFloat(formData.per_unit_price) || 0;
    const units = field === "sms_units" ? parseInt(value, 10) || 0 : parseInt(formData.sms_units, 10) || 0;
    const amount = field === "amount" ? parseFloat(value) || 0 : parseFloat(formData.amount) || 0;

    const next = { ...formData, [field]: value };

    if (!perUnitPrice || perUnitPrice <= 0) {
      setFormData(next);
      return;
    }

    if (field === "sms_units") {
      const u = parseInt(value, 10) || 0;
      if (u > 0) {
        next.amount = (u * perUnitPrice).toFixed(2);
        setCalculationMode("units");
      } else {
        next.amount = "";
      }
    } else if (field === "amount") {
      const a = parseFloat(value) || 0;
      if (a > 0) {
        next.sms_units = String(Math.floor(a / perUnitPrice));
        setCalculationMode("amount");
      } else {
        next.sms_units = "";
      }
    } else if (field === "per_unit_price") {
      const p = parseFloat(value) || 0;
      if (p > 0) {
        if (calculationMode === "units" && units > 0) {
          next.amount = (units * p).toFixed(2);
        } else if (calculationMode === "amount" && amount > 0) {
          next.sms_units = String(Math.floor(amount / p));
        } else if (units > 0) {
          next.amount = (units * p).toFixed(2);
          setCalculationMode("units");
        } else if (amount > 0) {
          next.sms_units = String(Math.floor(amount / p));
          setCalculationMode("amount");
        }
      }
    }

    setFormData(next);
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // User selection validation
    if (!formData.user_slug || formData.user_slug.trim() === "") {
      newErrors.user_slug = "Please search and select a user";
    }

    // Per unit price validation
    if (!formData.per_unit_price || formData.per_unit_price.trim() === "") {
      newErrors.per_unit_price = "Price per unit is required";
    } else if (parseFloat(formData.per_unit_price) <= 0) {
      newErrors.per_unit_price = "Price per unit must be greater than 0";
    }

    // SMS units validation
    if (!formData.sms_units || formData.sms_units.trim() === "") {
      newErrors.sms_units = "SMS units is required";
    } else if (parseInt(formData.sms_units) <= 0) {
      newErrors.sms_units = "SMS units must be greater than 0";
    }

    // Amount validation
    if (!formData.amount || formData.amount.trim() === "") {
      newErrors.amount = "Total amount is required";
    } else if (parseFloat(formData.amount) <= 0) {
      newErrors.amount = "Total amount must be greater than 0";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Check if form is complete (for button state)
  const isFormComplete = () => {
    return (
      formData.user_slug &&
      formData.user_slug.trim() !== "" &&
      formData.per_unit_price &&
      formData.per_unit_price.trim() !== "" &&
      parseFloat(formData.per_unit_price) > 0 &&
      formData.sms_units &&
      formData.sms_units.trim() !== "" &&
      parseInt(formData.sms_units, 10) > 0 &&
      formData.amount &&
      formData.amount.trim() !== "" &&
      parseFloat(formData.amount) > 0
    );
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        user_slug: formData.user_slug,
        amount: formData.amount,
        sms_units: parseInt(formData.sms_units),
        per_unit_price: formData.per_unit_price,
        receipt_url: formData.receipt_url,
      };

      const response = await postPaidManagement.createPostPaidRequest(payload);

      if (response.status) {
        toast.success(
          response.data?.data?.reason ||
            response.message ||
            "Custom-plan request created successfully!"
        );
        navigate("/postpaid-requests");
      } else {
        toast.error(
          response.data?.data?.reason ||
            response.message ||
            "Failed to create custom-plan request"
        );
      }
    } catch (error) {
      console.error("Error creating custom-plan request:", error);
      toast.error("Failed to create custom-plan request");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50">
        <div className="px-4 py-8 mx-auto max-w-8xl sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex gap-2 items-center sm:gap-3">
                <div className="p-2 bg-red-600 rounded-xl shadow-lg sm:p-3">
                  <Plus className="w-5 h-5 text-white sm:w-6 sm:h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <h1 className="text-xl font-bold leading-tight text-gray-900 sm:text-2xl lg:text-3xl">
                    Create Custom-Plan Request
                  </h1>
                  <p className="mt-1 text-sm text-gray-600 sm:text-base">
                    Create a new request for custom-plan requests
                  </p>
                </div>
              </div>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate("/postpaid-requests")}
                className="flex gap-2 items-center self-start px-3 py-2 text-xs font-medium text-gray-600 whitespace-nowrap bg-white rounded-lg shadow-sm transition-colors sm:self-center sm:px-4 sm:text-sm hover:bg-gray-50"
              >
                <ArrowLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Back to Requests</span>
                <span className="sm:hidden">Back</span>
              </motion.button>
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="overflow-hidden relative bg-white rounded-2xl border border-gray-100 shadow-xl"
          >
            <form onSubmit={handleSubmit} className="p-8">
              <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
                {/* Left Column - Form Fields */}
                <div className="order-1 space-y-6 lg:col-span-2">
                  {/* User Search */}
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      <div className="flex gap-2 items-center">
                        <Search className="w-4 h-4 text-red-600" />
                        Search User <span className="text-red-600">*</span>
                      </div>
                    </label>
                    <div className="relative" ref={searchInputRef}>
                      <div className="relative">
                        <Search className="absolute left-4 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => handleSearchChange(e.target.value)}
                          onFocus={() => {
                            if (searchedUsers.length > 0) {
                              setShowUserList(true);
                            }
                          }}
                          placeholder="Search by email, phone number, or name..."
                          className={`w-full pl-12 pr-12 py-3 border rounded-xl focus:ring-2 focus:ring-inset focus:ring-red-500 focus:border-transparent focus:outline-none transition-colors box-border ${
                            errors.user_slug
                              ? "border-red-300 bg-red-50"
                              : searchError
                              ? "border-orange-300 bg-orange-50"
                              : selectedUser
                              ? "border-green-300 bg-green-50"
                              : "border-gray-200"
                          }`}
                        />
                        {searchQuery && (
                          <button
                            type="button"
                            onClick={clearSearch}
                            className="absolute right-3 top-1/2 p-1 text-gray-400 transition-colors transform -translate-y-1/2 hover:text-gray-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                        {searching && (
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                            <Loader2 className="w-4 h-4 text-red-600 animate-spin" />
                          </div>
                        )}
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        Enter email address, phone number, or name. Type at least 2 characters to search.
                      </p>

                      {/* User List Dropdown */}
                      {showUserList && searchedUsers.length > 0 && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="overflow-y-auto absolute z-50 mt-2 w-full max-h-96 bg-white rounded-xl border border-gray-200 shadow-lg user-list-container"
                        >
                          <div className="p-3 bg-gray-50 border-b border-gray-100">
                            <div className="flex gap-2 items-center">
                              <Users className="w-4 h-4 text-gray-500" />
                              <span className="text-sm font-medium text-gray-700">
                                Found {searchedUsers.length} user{searchedUsers.length !== 1 ? "s" : ""}
                              </span>
                            </div>
                          </div>
                          <div className="divide-y divide-gray-100">
                            {searchedUsers.map((user, index) => (
                              <motion.div
                                key={user.user_slug || index}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: index * 0.05 }}
                                className="p-4 transition-colors cursor-pointer hover:bg-gray-50"
                                onClick={() => handleSelectUser(user)}
                              >
                                <div className="flex gap-3 items-center">
                                  <div className="flex-shrink-0">
                                    <div className="flex justify-center items-center w-10 h-10 bg-red-100 rounded-full">
                                      <span className="text-sm font-semibold text-red-600">
                                        {user.full_name?.charAt(0)?.toUpperCase() ||
                                          user.email?.charAt(0)?.toUpperCase() ||
                                          user.phone_number?.charAt(0)?.toUpperCase() ||
                                          "U"}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex gap-2 items-center mb-1">
                                      <h4 className="text-sm font-semibold text-gray-900 truncate">
                                        {user.full_name || "No Name"}
                                      </h4>
                                      {selectedUser?.user_slug === user.user_slug && (
                                        <CheckCircle className="flex-shrink-0 w-3 h-3 text-green-500" />
                                      )}
                                    </div>
                                    <div className="space-y-1">
                                      {user.email && (
                                        <div className="flex gap-2 items-center">
                                          <Mail className="flex-shrink-0 w-3 h-3 text-gray-400" />
                                          <span className="text-xs text-gray-600 truncate">
                                            {user.email}
                                          </span>
                                        </div>
                                      )}
                                      {user.phone_number && (
                                        <div className="flex gap-2 items-center">
                                          <Phone className="flex-shrink-0 w-3 h-3 text-gray-400" />
                                          <span className="text-xs text-gray-600">
                                            {user.phone_number}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSelectUser(user);
                                    }}
                                    className="flex gap-1 items-center px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded-lg transition-colors hover:bg-red-700"
                                  >
                                    <UserPlus className="w-3 h-3" />
                                    Select
                                  </button>
                                </div>
                              </motion.div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </div>

                    {/* Error Message */}
                    {searchError && !searching && !showUserList && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-4 mt-3 bg-orange-50 rounded-xl border border-orange-200"
                      >
                        <div className="flex gap-3 items-start">
                          <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-orange-800">
                              No Users Found
                            </p>
                            <p className="mt-1 text-xs text-orange-700">
                              {searchError}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {/* Selected User Card */}
                    {selectedUser && !showUserList && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-3 mt-3 bg-gradient-to-br from-green-50 to-green-100 rounded-xl border-2 border-green-300 shadow-md sm:p-5"
                      >
                        <div className="flex flex-col gap-3 items-start sm:flex-row sm:items-start">
                          <div className="flex-shrink-0 self-center sm:self-start">
                            <div className="flex justify-center items-center w-12 h-12 bg-red-600 rounded-full shadow-lg sm:w-14 sm:h-14">
                              <span className="text-lg font-bold text-white sm:text-xl">
                                {selectedUser.full_name
                                  ?.charAt(0)
                                  ?.toUpperCase() ||
                                  selectedUser.email
                                    ?.charAt(0)
                                    ?.toUpperCase() ||
                                  "U"}
                              </span>
                            </div>
                          </div>
                          <div className="flex-1 w-full min-w-0">
                            <div className="flex gap-2 justify-between items-center mb-2 sm:mb-3">
                              <div className="flex gap-2 items-center">
                                <CheckCircle className="flex-shrink-0 w-5 h-5 text-green-600 sm:w-6 sm:h-6" />
                                <h4 className="text-sm font-bold leading-tight text-gray-900 whitespace-nowrap sm:text-base lg:text-lg">
                                  User Selected
                                </h4>
                              </div>
                              <button
                                type="button"
                                onClick={clearSearch}
                                className="p-1 text-gray-400 transition-colors hover:text-gray-600"
                                title="Change user"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                            <div className="space-y-2 bg-white p-2.5 rounded-lg border border-green-200 sm:space-y-2.5 sm:p-3">
                              {selectedUser.full_name && (
                                <div className="flex gap-2 items-start">
                                  <Users className="flex-shrink-0 w-4 h-4 text-red-600 mt-0.5" />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs text-gray-500 mb-0.5">
                                      Full Name
                                    </p>
                                    <span className="text-xs font-semibold text-gray-900 break-all sm:text-sm">
                                      {selectedUser.full_name}
                                    </span>
                                  </div>
                                </div>
                              )}
                              {selectedUser.email && (
                                <div className="flex gap-2 items-start">
                                  <Mail className="flex-shrink-0 w-4 h-4 text-red-600 mt-0.5" />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs text-gray-500 mb-0.5">
                                      Email
                                    </p>
                                    <span className="text-xs font-semibold text-gray-900 break-all sm:text-sm">
                                      {selectedUser.email}
                                    </span>
                                  </div>
                                </div>
                              )}
                              {selectedUser.phone_number && (
                                <div className="flex gap-2 items-start">
                                  <Phone className="flex-shrink-0 w-4 h-4 text-red-600 mt-0.5" />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs text-gray-500 mb-0.5">
                                      Phone Number
                                    </p>
                                    <span className="text-xs font-semibold text-gray-900 break-all sm:text-sm">
                                      {selectedUser.phone_number}
                                    </span>
                                  </div>
                                </div>
                              )}
                              {selectedUser.user_slug && (
                                <div className="flex gap-2 items-start pt-2 border-t border-gray-200">
                                  <Hash className="flex-shrink-0 w-4 h-4 text-red-600 mt-0.5" />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs text-gray-500 mb-0.5">
                                      User ID
                                    </p>
                                    <span className="font-mono text-xs text-gray-900 break-all">
                                      {selectedUser.user_slug}
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {errors.user_slug && (
                      <p className="flex gap-1 items-center mt-2 text-sm text-red-600">
                        <AlertCircle className="w-4 h-4" />
                        {errors.user_slug}
                      </p>
                    )}
                  </div>

                  {/* Calculation Info Banner */}
                  {(formData.sms_units || formData.amount) &&
                    formData.per_unit_price && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="p-4 bg-green-50 rounded-xl border border-green-200"
                      >
                        <div className="flex gap-2 items-center mb-2">
                          <Calculator className="w-5 h-5 text-green-600" />
                          <p className="text-sm font-semibold text-green-800">
                            Auto-Calculation Active
                          </p>
                        </div>
                        <p className="text-xs text-green-700">
                          {calculationMode === "units"
                            ? "Amount calculated from SMS units"
                            : calculationMode === "amount"
                            ? "SMS units calculated from amount"
                            : "Enter SMS units or amount to calculate"}
                        </p>
                      </motion.div>
                    )}

                  {/* Per Unit Price */}
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      <div className="flex gap-2 items-center">
                        <DollarSign className="w-4 h-4 text-red-600" />
                        Price Per Unit (GH₵){" "}
                        <span className="text-red-600">*</span>
                      </div>
                    </label>
                    <input
                      type="number"
                      step="any"
                      min="0"
                      value={formData.per_unit_price}
                      onChange={(e) =>
                        handleFieldChange("per_unit_price", e.target.value)
                      }
                      placeholder="0.0346"
                      className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-inset focus:ring-red-500 focus:border-transparent focus:outline-none transition-colors box-border ${
                        errors.per_unit_price
                          ? "border-red-300 bg-red-50"
                          : "border-gray-200"
                      }`}
                    />
                    {errors.per_unit_price && (
                      <p className="flex gap-1 items-center mt-1 text-sm text-red-600">
                        <AlertCircle className="w-4 h-4" />
                        {errors.per_unit_price}
                      </p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      Enter the price per SMS unit
                    </p>
                  </div>

                  {/* SMS Units and Amount - Side by Side */}
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    {/* SMS Units */}
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">
                        <div className="flex gap-2 items-center">
                          <Hash className="w-4 h-4 text-red-600" />
                          SMS Units <span className="text-red-600">*</span>
                        </div>
                      </label>
                      <input
                        type="number"
                        step="1"
                        min="0"
                        value={formData.sms_units}
                        onChange={(e) => {
                          setCalculationMode("units");
                          handleFieldChange("sms_units", e.target.value);
                        }}
                        placeholder="7888"
                        className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-inset focus:ring-red-500 focus:border-transparent focus:outline-none transition-colors box-border ${
                          errors.sms_units
                            ? "border-red-300 bg-red-50"
                            : "border-gray-200"
                        }`}
                      />
                      {errors.sms_units && (
                        <p className="flex gap-1 items-center mt-1 text-sm text-red-600">
                          <AlertCircle className="w-4 h-4" />
                          {errors.sms_units}
                        </p>
                      )}
                      {calculationMode === "units" && formData.amount && (
                        <p className="flex gap-1 items-center mt-1 text-xs text-green-600">
                          <CheckCircle2 className="w-3 h-3" />
                          Amount: GH₵{formData.amount}
                        </p>
                      )}
                    </div>

                    {/* Total Amount */}
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">
                        <div className="flex gap-2 items-center">
                          <DollarSign className="w-4 h-4 text-red-600" />
                          Total Amount (GH₵){" "}
                          <span className="text-red-600">*</span>
                        </div>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.amount}
                        onChange={(e) => {
                          setCalculationMode("amount");
                          handleFieldChange("amount", e.target.value);
                        }}
                        placeholder="100.00"
                        className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-inset focus:ring-red-500 focus:border-transparent focus:outline-none transition-colors box-border ${
                          errors.amount
                            ? "border-red-300 bg-red-50"
                            : "border-gray-200"
                        }`}
                      />
                      {errors.amount && (
                        <p className="flex gap-1 items-center mt-1 text-sm text-red-600">
                          <AlertCircle className="w-4 h-4" />
                          {errors.amount}
                        </p>
                      )}
                      {calculationMode === "amount" && formData.sms_units && (
                        <p className="flex gap-1 items-center mt-1 text-xs text-green-600">
                          <CheckCircle2 className="w-3 h-3" />
                          Units: {formData.sms_units}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Summary Card */}
                  {selectedUser &&
                    formData.amount &&
                    formData.sms_units &&
                    formData.per_unit_price && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-6 bg-red-50 rounded-xl border border-red-200"
                      >
                        <h3 className="mb-4 text-lg font-semibold text-gray-900">
                          Request Summary
                        </h3>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">User:</span>
                            <span className="text-sm font-semibold text-gray-900">
                              {selectedUser.full_name || selectedUser.email}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">
                              SMS Units:
                            </span>
                            <span className="text-sm font-semibold text-gray-900">
                              {parseInt(formData.sms_units).toLocaleString()}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">
                              Price Per Unit:
                            </span>
                            <span className="text-sm font-semibold text-gray-900">
                              GH₵
                              {parseFloat(formData.per_unit_price).toFixed(4)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center pt-2 border-t border-red-200">
                            <span className="text-base font-semibold text-gray-900">
                              Total Amount:
                            </span>
                            <span className="text-xl font-bold text-red-600">
                              GH₵{parseFloat(formData.amount).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </motion.div>
                    )}

                  {/* Receipt Image Upload - Mobile Only (before buttons) */}
                  <div className="lg:hidden">
                    <label className="block mb-3 text-sm font-medium text-gray-700">
                      <div className="flex gap-2 items-center">
                        <ImageIcon className="w-5 h-5 text-red-600" />
                        <span className="text-base font-semibold">
                          Receipt Image
                        </span>
                      </div>
                    </label>

                    {!receiptPreview ? (
                      <div
                        className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 ${
                          errors.receipt_url
                            ? "border-red-300 bg-red-50"
                            : "border-gray-300 bg-gray-50 hover:border-red-400 hover:bg-red-50 hover:shadow-lg"
                        }`}
                      >
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleReceiptUpload}
                          className="absolute inset-0 z-10 w-full h-full opacity-0 cursor-pointer"
                          disabled={uploadingImage}
                        />
                        <div className="flex flex-col gap-3 items-center">
                          {uploadingImage ? (
                            <>
                              <Loader2 className="w-10 h-10 text-red-600 animate-spin" />
                              <p className="text-sm font-medium text-gray-700">
                                Processing image...
                              </p>
                            </>
                          ) : (
                            <>
                              <div className="p-4 bg-red-100 rounded-full shadow-md">
                                <Upload className="w-8 h-8 text-red-600" />
                              </div>
                              <div>
                                <p className="mb-1 text-base font-semibold text-gray-800">
                                  Click to upload receipt
                                </p>
                                <p className="text-xs text-gray-500">
                                  PNG, JPG, or JPEG
                                </p>
                                <p className="mt-1 text-xs text-gray-400">
                                  Maximum file size: 5MB
                                </p>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    ) : (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="overflow-hidden relative bg-white rounded-xl border-2 border-gray-200 shadow-lg"
                      >
                        <div className="relative bg-gray-100">
                          <img
                            src={receiptPreview}
                            alt="Receipt preview"
                            className="object-contain w-full h-auto max-h-[450px] min-h-[250px]"
                          />
                          <button
                            type="button"
                            onClick={handleRemoveReceipt}
                            className="absolute top-4 right-4 p-2.5 text-white bg-red-600 rounded-lg shadow-xl transition-all hover:bg-red-700 hover:scale-110 z-10"
                            title="Remove image"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                        <div className="p-4 bg-gradient-to-r from-green-50 to-green-100 border-t border-green-200">
                          <div className="flex justify-between items-center">
                            <div className="flex gap-2 items-center">
                              <CheckCircle2 className="w-5 h-5 text-green-600" />
                              <div>
                                <p className="text-sm font-semibold text-gray-800">
                                  {receiptImage?.name?.length > 20
                                    ? `${receiptImage.name.substring(0, 20)}...`
                                    : receiptImage?.name || "Receipt uploaded"}
                                </p>
                                <p className="text-xs text-gray-600 mt-0.5">
                                  Image ready
                                </p>
                              </div>
                            </div>
                            <span className="px-2 py-1 text-xs font-medium text-gray-600 bg-white rounded">
                              {receiptImage?.size
                                ? `${(receiptImage.size / 1024 / 1024).toFixed(2)} MB`
                                : ""}
                            </span>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {errors.receipt_url && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-3 mt-3 bg-red-50 rounded-lg border border-red-200"
                      >
                        <p className="flex gap-2 items-center text-sm text-red-600">
                          <AlertCircle className="flex-shrink-0 w-4 h-4" />
                          <span>{errors.receipt_url}</span>
                        </p>
                      </motion.div>
                    )}
                    <p className="mt-3 text-xs leading-relaxed text-gray-500">
                      Upload a clear image of the payment receipt. The image
                      will aid the financial team in verifying the payment.
                    </p>
                  </div>

                  {/* Form Completion Status */}
                  {!isFormComplete() && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-3 bg-orange-50 rounded-lg border border-orange-200"
                    >
                      <div className="flex gap-2 items-center">
                        <AlertCircle className="flex-shrink-0 w-4 h-4 text-orange-600" />
                        <p className="text-xs text-orange-800">
                          Please fill in all required fields (marked with{" "}
                          <span className="font-bold text-red-600">*</span>)
                          before submitting
                        </p>
                      </div>
                    </motion.div>
                  )}

                  {/* Submit Buttons */}
                  <div className="flex flex-col gap-3 pt-4 sm:flex-row sm:gap-4">
                    <motion.button
                      type="button"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => navigate("/postpaid-requests")}
                      className="w-full sm:flex-1 px-4 py-2.5 sm:px-6 sm:py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl transition-colors hover:bg-gray-200"
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                      type="submit"
                      whileHover={{
                        scale: isFormComplete() && !saving ? 1.02 : 1,
                      }}
                      whileTap={{
                        scale: isFormComplete() && !saving ? 0.98 : 1,
                      }}
                      disabled={saving || !isFormComplete()}
                      className="w-full sm:flex-1 flex gap-2 justify-center items-center px-4 py-2.5 sm:px-6 sm:py-3 text-sm font-medium text-white bg-red-600 rounded-xl transition-colors hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      title={
                        !isFormComplete()
                          ? "Please fill in all required fields"
                          : ""
                      }
                    >
                      {saving ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Creating...</span>
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          <span>Create Request</span>
                        </>
                      )}
                    </motion.button>
                  </div>
                </div>

                {/* Right Column - Receipt Image Upload (Desktop Only) */}
                <div className="hidden order-2 lg:block lg:col-span-1">
                  <label className="block mb-3 text-sm font-medium text-gray-700">
                    <div className="flex gap-2 items-center">
                      <ImageIcon className="w-5 h-5 text-red-600" />
                      <span className="text-base font-semibold">
                        Receipt Image
                      </span>
                    </div>
                  </label>

                  {!receiptPreview ? (
                    <div
                      className={`relative border-2 border-dashed rounded-xl p-12 text-center transition-all duration-300 ${
                        errors.receipt_url
                          ? "border-red-300 bg-red-50"
                          : "border-gray-300 bg-gray-50 hover:border-red-400 hover:bg-red-50 hover:shadow-lg"
                      }`}
                    >
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleReceiptUpload}
                        className="absolute inset-0 z-10 w-full h-full opacity-0 cursor-pointer"
                        disabled={uploadingImage}
                      />
                      <div className="flex flex-col gap-4 items-center">
                        {uploadingImage ? (
                          <>
                            <Loader2 className="w-12 h-12 text-red-600 animate-spin" />
                            <p className="text-sm font-medium text-gray-700">
                              Processing image...
                            </p>
                          </>
                        ) : (
                          <>
                            <div className="p-5 bg-red-100 rounded-full shadow-md">
                              <Upload className="w-10 h-10 text-red-600" />
                            </div>
                            <div>
                              <p className="mb-1 text-base font-semibold text-gray-800">
                                Click to upload receipt
                              </p>
                              <p className="text-xs text-gray-500">
                                PNG, JPG, or JPEG
                              </p>
                              <p className="mt-1 text-xs text-gray-400">
                                Maximum file size: 5MB
                              </p>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="overflow-hidden relative bg-white rounded-xl border-2 border-gray-200 shadow-lg"
                    >
                      <div className="relative bg-gray-100">
                        <img
                          src={receiptPreview}
                          alt="Receipt preview"
                          className="object-contain w-full h-auto max-h-[500px] min-h-[300px]"
                        />
                        <button
                          type="button"
                          onClick={handleRemoveReceipt}
                          className="absolute top-4 right-4 p-2.5 text-white bg-red-600 rounded-lg shadow-xl transition-all hover:bg-red-700 hover:scale-110 z-10"
                          title="Remove image"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                      <div className="p-4 bg-gradient-to-r from-green-50 to-green-100 border-t border-green-200">
                        <div className="flex justify-between items-center">
                          <div className="flex gap-2 items-center">
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                            <div>
                              <p className="text-sm font-semibold text-gray-800">
                                {receiptImage?.name?.length > 20
                                  ? `${receiptImage.name.substring(0, 20)}...`
                                  : receiptImage?.name || "Receipt uploaded"}
                              </p>
                              <p className="text-xs text-gray-600 mt-0.5">
                                Image ready
                              </p>
                            </div>
                          </div>
                          <span className="px-2 py-1 text-xs font-medium text-gray-600 bg-white rounded">
                            {receiptImage?.size
                              ? `${(receiptImage.size / 1024 / 1024).toFixed(
                                  2
                                )} MB`
                              : ""}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {errors.receipt_url && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-3 mt-3 bg-red-50 rounded-lg border border-red-200"
                    >
                      <p className="flex gap-2 items-center text-sm text-red-600">
                        <AlertCircle className="flex-shrink-0 w-4 h-4" />
                        <span>{errors.receipt_url}</span>
                      </p>
                    </motion.div>
                  )}
                  <p className="mt-3 text-xs leading-relaxed text-gray-500">
                    Upload a clear image of the payment receipt. The image will
                    aid the financial team in verifying the payment.
                  </p>
                </div>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default CreatePostPaidRequest;